import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { CampaignOverviewComponent } from '../campaign-overview/campaign-overview.component';
import { CampaignOffersComponent } from '../campaign-offers/campaign-offers.component';
import { CampaignContentComponent } from '../campaign-content/campaign-content.component';
import { CampaignGoalsComponent } from '../campaign-goals/campaign-goals.component';
import { CampaignScheduleComponent } from '../campaign-schedule/campaign-schedule.component';
import { CampaignLeadsComponent } from '../campaign-leads/campaign-leads.component';
import {
  CampaignService,
  Campaign,
  getCampaignTrackingEventTypesQueryParam,
} from '../../services/campaign.service';
import {
  findCommonWebsiteContent,
  hasLandingPageApiContent,
  hasSuccessPageApiContent,
} from '../campaign-editor/optional-setting/common-website-content.mapper';
import {
  findDeliveryEmailContent,
  findMmsContent,
  findRcsContent,
  findSmsDeliveryContent,
  hasEmailDeliveryApiContent,
  hasMmsDeliveryApiContent,
  hasRcsDeliveryApiContent,
  hasSmsDeliveryApiContent,
} from '../campaign-editor/optional-setting/sms-delivery.mapper';
import { DuplicateCampaignModalComponent } from '../duplicate-campaign-modal/duplicate-campaign-modal.component';
import { ArchiveCampaignModalComponent } from '../archive-campaign-modal/archive-campaign-modal.component';
import { PauseCampaignModalComponent } from '../pause-campaign-modal/pause-campaign-modal.component';
import { StopCampaignModalComponent } from '../stop-campaign-modal/stop-campaign-modal.component';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Router } from '@angular/router';

// Campaign interface is now imported from CampaignService

interface Tab {
  id: string;
  label: string;
  icon: string;
  active: boolean;
}

/**
 * Modal component for viewing detailed information about a campaign, navigating campaign tabs (Overview, Offers, Content, Goals, Schedule), and performing management actions (edit, duplicate, archive, pause/resume, stop, mark complete).
 * Key responsibilities: load campaign data by ID, present tabbed campaign details, show action menu and confirmation modals, and emit events when the parent should refresh or respond to changes.
 * Lifecycle:
 *   - ngOnInit: initializes the active tab to "overview" and loads campaign data if campaignId is provided.
 * @component
 * @selector app-view-campaign-modal
 * @example
 *   <app-view-campaign-modal [campaignId]="selectedId" [visible]="isModalVisible" (close)="onClose()" (edit)="onEdit($event)"></app-view-campaign-modal>
 * @input {string | null} campaignId - The ID of the campaign to load and display. If provided, the component will fetch campaign data on init and when changed.
 * @input {boolean} visible - Controls modal visibility. When false the modal is hidden.
 * @output {EventEmitter<void>} close - Emitted when the modal should be closed (user action or after successful operations).
 * @output {EventEmitter<Campaign>} edit - Emitted when the user chooses to edit the currently viewed campaign; payload is the Campaign object.
 * @output {EventEmitter<void>} campaignDuplicated - Emitted after a campaign has been successfully duplicated; parent can refresh list.
 * @output {EventEmitter<void>} campaignArchived - Emitted after a campaign has been archived; parent can refresh list.
 * @output {EventEmitter<void>} campaignPaused - Emitted after a campaign has been paused or resumed; parent can refresh list.
 * @output {EventEmitter<void>} campaignStopped - Emitted after a campaign has been stopped; parent can refresh list.
 * @output {EventEmitter<void>} campaignMarkedComplete - Emitted when the campaign is marked complete; typically used to navigate or refresh parent views.
 * Important methods:
 *   - loadCampaign(): Fetches campaign data from CampaignService by campaignId, manages loading and error state.
 *   - onClose(): Emits the close event to the parent.
 *   - showActionMenu(event): Toggles the action Popover for campaign actions (prevents default propagation).
 *   - onEdit(): Hides action menu and emits edit with the current campaign.
 *   - onDuplicateCampaign(), onConfirmDuplicate(), onCloseDuplicateModal(): Manage duplicate flow and call CampaignService to create a duplicated campaign; shows success/error messages and emits campaignDuplicated on success.
 *   - onArchiveCampaign(), onConfirmArchive(), onCloseArchiveModal(): Manage archive confirmation and call CampaignService.archiveCampaign; updates local status and emits campaignArchived on success.
 *   - onPauseCampaign(), onConfirmPause(), onClosePauseModal(): Toggles pause/resume state via CampaignService.updateCampaign; updates local status and emits campaignPaused on success.
 *   - onStopCampaign(), onConfirmStop(), onCloseStopModal(): Stops campaign via CampaignService.updateCampaign; updates local status and emits campaignStopped on success.
 *   - onMarkComplete(): Emits campaignMarkedComplete and navigates to dashboard with campaign management tab.
 *   - setActiveTab(tabId): Switches active tab and updates internal tabs array active flags.
 *   - getStatusDisplay(status): Delegates to CampaignService.getStatusDisplay to produce a human readable status.
 *   - getStatusClass(status): Returns CSS class name for a given campaign status for UI styling.
 * Notes:
 *   - Uses NotificationService to show user feedback for success/error of operations.
 *   - Interacts with several confirmation/utility modals (duplicate, archive, pause, stop) and an Popover for actions.
 */
@Component({
  selector: 'app-view-campaign-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    CampaignOverviewComponent,
    CampaignOffersComponent,
    CampaignContentComponent,
    CampaignGoalsComponent,
    CampaignScheduleComponent,
    CampaignLeadsComponent,
    DuplicateCampaignModalComponent,
    ArchiveCampaignModalComponent,
    PauseCampaignModalComponent,
    StopCampaignModalComponent,
  ],
  templateUrl: './view-campaign-modal.component.html',
  styleUrls: ['./view-campaign-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ViewCampaignModalComponent implements OnInit {
  @Input() campaignId: string | null = null;
  @Input() visible = false;
  @Output() close = new EventEmitter<void>();
  @Output() edit = new EventEmitter<Campaign>();
  @Output() campaignDuplicated = new EventEmitter<void>();
  @Output() campaignArchived = new EventEmitter<void>();
  @Output() campaignPaused = new EventEmitter<void>();
  @Output() campaignStopped = new EventEmitter<void>();
  @Output() campaignMarkedComplete = new EventEmitter<void>();

  campaign: Campaign | null = null;
  loading = false;
  error: string | null = null;
  showDuplicateModal = false;
  showArchiveModal = false;
  showPauseModal = false;
  showStopModal = false;

  showLeadsTab = false;
  tabsResolved = false;
  hasAdvanceContentFlag = false;
  hasLandingPageContentFlag = false;

  tabs: Tab[] = this.buildTabs(false, false);

  activeTab = 'overview';

  constructor(
    private campaignService: CampaignService,
    private messageService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Initialize with overview tab active
    this.setActiveTab('overview');

    // Load campaign data if campaignId is provided
    if (this.campaignId) {
      this.loadCampaign();
    }
  }

  /**
   * Load campaign data for the current campaignId, update component state, and handle loading/error conditions.
   * @example
   * oadCampaign()
   * // void
   * @returns {void} Does not return a value; sets this.campaign, this.loading, and this.error as appropriate.
   */
  loadCampaign(): void {
    if (!this.campaignId) return;

    this.loading = true;
    this.error = null;

    this.campaignService.getCampaign(this.campaignId).subscribe({
      next: response => {
        this.campaign = response.data || null;
        this.updateAdvanceContentState();
        this.loading = false;
        if (this.campaign?.id) {
          this.resolveLeadsTabVisibility(this.campaign.id);
        } else {
          this.applyTabConfiguration(false);
        }
      },
      error: error => {
        this.error = 'Failed to load campaign data';
        this.loading = false;
        console.error('Error loading campaign:', error);
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  showActionMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    // Action menu is now handled via Material menu in template
  }

  onEdit(): void {
    if (this.campaign) {
      this.edit.emit(this.campaign);
    }
  }

  onDuplicateCampaign(): void {
    if (this.campaign) {
      this.showDuplicateModal = true;
    }
  }

  /**
   * Duplicate a campaign by building a duplicate payload, sending a create request, and handling success/error UI updates and events.
   * @example
   * nConfirmDuplicate({ id: 'abc123', name: 'Spring Sale' })
   * undefined
   * @param {{any}} {{campaign}} - Campaign object to duplicate.
   * @returns {{void}} Performs creation and UI side-effects (notifications, modal state, and emits a duplication event); no return value.
   **/
  onConfirmDuplicate(campaign: any): void {
    const duplicateCampaign = CampaignService.buildDuplicatePayload(campaign);

    this.campaignService.createCampaign(duplicateCampaign).subscribe({
      next: response => {
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'New campaign created!',
            detail: `"${campaign.name}" has been added successfully.`,
          });
          // Close duplicate modal
          this.showDuplicateModal = false;
          // Emit event to notify parent component to refresh campaigns
          this.campaignDuplicated.emit();
          // Close the view campaign modal
          this.onClose();
        } else {
          console.error('Failed to duplicate campaign:', response.message);
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to duplicate campaign!',
            detail: response.message,
          });
        }
      },
      error: error => {
        console.error('Error duplicating campaign:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to duplicate campaign!',
          detail: error.error?.message || 'An error occurred while duplicating the campaign.',
        });
      },
    });
  }

  onCloseDuplicateModal(): void {
    this.showDuplicateModal = false;
  }

  onArchiveCampaign(): void {
    if (this.campaign) {
      this.showArchiveModal = true;
    }
  }

  /**
   * Confirm and archive a campaign by calling the campaignService, updating local state, showing a success message, emitting a campaignArchived event, and closing the archive modal.
   * @example
   * nConfirmArchive({ id: 123, name: 'Spring Sale' })
   * undefined
   * @param {{any}} {{campaign}} - Campaign object containing at least an id and name to identify the campaign to archive.
   * @returns {{void}} No return value; performs side effects (HTTP request, message display, event emission, and modal close).
   **/
  onConfirmArchive(campaign: any): void {
    if (!campaign) return;

    this.campaignService.archiveCampaign(campaign.id, campaign.name).subscribe({
      next: response => {
        if (response.status) {
          // Update the campaign status locally
          if (this.campaign) {
            this.campaign.campaign_status = 'archived';
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Campaign archived!',
            detail: `"${campaign.name}" has been archived successfully.`,
          });
          // Emit event to notify parent component to refresh campaigns
          this.campaignArchived.emit();
        } else {
          console.error('Failed to archive campaign:', response.message);
        }
      },
      error: error => {
        console.error('Error archiving campaign:', error);
      },
    });

    this.showArchiveModal = false;
  }

  onCloseArchiveModal(): void {
    this.showArchiveModal = false;
  }

  onPauseCampaign(): void {
    if (this.campaign) {
      this.showPauseModal = true;
    }
  }

  /**
   * Toggle pause/resume for the given campaign: updates the campaign status via the campaignService, shows a success or error message, emits an event to refresh parent data, and closes the pause modal.
   * @example
   * nConfirmPause({ id: 123, campaign_status: 'paused', name: 'Spring Sale' })
   * void
   * @param {{any}} {{campaign}} - Campaign object containing at minimum id, campaign_status and name.
   * @returns {{void}} No return value.
   **/
  onConfirmPause(campaign: any): void {
    const newStatus = campaign.campaign_status === 'paused' ? 'scheduled' : 'paused';

    const updatePayload = {
      campaign_status: newStatus,
      name: campaign.name,
    };

    this.campaignService.updateCampaign(campaign.id, updatePayload).subscribe({
      next: response => {
        if (response.status) {
          // Update the campaign status locally
          if (this.campaign) {
            this.campaign.campaign_status = newStatus;
          }
          this.messageService.add({
            severity: 'success',
            summary: `Campaign ${newStatus === 'paused' ? 'paused' : 'resumed'}!`,
            detail: `"${campaign.name}" has been ${newStatus === 'paused' ? 'paused' : 'resumed'} successfully.`,
          });

          // Emit event to notify parent component to refresh campaigns
          this.campaignPaused.emit();
        } else {
          console.error('Failed to update campaign status:', response.message);
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to update campaign!',
            detail: response.message,
          });
        }
      },
      error: error => {
        console.error('Error updating campaign status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to update campaign!',
          detail: error.error?.message || 'An error occurred while updating the campaign.',
        });
      },
    });

    this.showPauseModal = false;
  }

  onClosePauseModal(): void {
    this.showPauseModal = false;
  }

  onStopCampaign(): void {
    if (this.campaign) {
      this.showStopModal = true;
    }
  }

  /**
   * Stops the given campaign by updating its status to 'stopped', shows a success/error message and emits an event to refresh campaigns.
   * @example
   * nConfirmStop({ id: '123', name: 'Spring Sale' })
   * void
   * @param {any} campaign - Campaign object (must include at least id and name) to be stopped.
   * @returns {void} Does not return a value.
   **/
  onConfirmStop(campaign: any): void {
    const updatePayload = {
      campaign_status: 'stopped',
      name: campaign.name,
    };

    this.campaignService.updateCampaign(campaign.id, updatePayload).subscribe({
      next: response => {
        if (response.status) {
          // Update the campaign status locally
          if (this.campaign) {
            this.campaign.campaign_status = 'stopped';
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Campaign stopped!',
            detail: `"${campaign.name}" has been stopped successfully.`,
          });

          // Emit event to notify parent component to refresh campaigns
          this.campaignStopped.emit();
        } else {
          console.error('Failed to stop campaign:', response.message);
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to stop campaign!',
            detail: response.message,
          });
        }
      },
      error: error => {
        console.error('Error stopping campaign:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to stop campaign!',
          detail: error.error?.message || 'An error occurred while stopping the campaign.',
        });
      },
    });

    this.showStopModal = false;
  }

  onCloseStopModal(): void {
    this.showStopModal = false;
  }

  onMarkComplete(): void {
    if (this.campaign) {
      // Emit event to notify parent component to refresh campaigns
      this.campaignMarkedComplete.emit();
      // Navigate to dashboard with campaign tab open
      this.router.navigate(['/dashboard'], { queryParams: { tab: 'Campaign Management' } });
    }
  }

  setActiveTab(tabId: string): void {
    this.activeTab = tabId;
    this.tabs.forEach(tab => {
      tab.active = tab.id === tabId;
    });
  }

  onEditAdvance(): void {
    if (!this.campaign?.id) {
      return;
    }
    this.onClose();
    this.router.navigate(['/campaign-editor', 'advanced', 'Optional Settings'], {
      queryParams: { campaignId: this.campaign.id },
    });
  }

  hasAdvanceContent(): boolean {
    return this.hasAdvanceContentFlag;
  }

  private buildTabs(showLeadsTab: boolean, includeSecondary: boolean): Tab[] {
    const tabs: Tab[] = [
      { id: 'overview', label: 'Overview', icon: 'clipboard-list', active: false },
    ];

    if (includeSecondary && showLeadsTab) {
      tabs.push({ id: 'leads', label: 'Leads', icon: 'users', active: false });
    }

    tabs.push(
      { id: 'offers', label: 'Offers', icon: 'tag', active: false },
      { id: 'content', label: 'Content', icon: 'pencil', active: false },
      { id: 'goals', label: 'Goals', icon: 'target', active: false }
    );

    if (includeSecondary && !showLeadsTab && !this.hasLandingPageContentFlag) {
      tabs.push({ id: 'advance', label: 'Advance', icon: 'settings', active: false });
    }

    tabs.push({ id: 'schedule', label: 'Schedule', icon: 'calendar', active: false });

    return tabs;
  }

  private resolveLeadsTabVisibility(campaignId: string): void {
    this.campaignService
      .getCampaignTrackingEvents(campaignId, {
        page: 1,
        limit: 1,
        event_type: getCampaignTrackingEventTypesQueryParam(),
      })
      .subscribe({
        next: response => {
          const total = response.data?.pagination?.total ?? 0;
          const hasLeads = Boolean(response.status && total > 0);
          this.applyTabConfiguration(hasLeads);
        },
        error: () => {
          this.applyTabConfiguration(false);
        },
      });
  }

  private applyTabConfiguration(showLeadsTab: boolean): void {
    const wasOnLeads = this.activeTab === 'leads';
    const wasOnAdvance = this.activeTab === 'advance';

    this.showLeadsTab = showLeadsTab;
    this.tabs = this.buildTabs(showLeadsTab, true);
    this.tabsResolved = true;

    if (showLeadsTab) {
      const nextTab =
        wasOnAdvance || !this.tabs.some(tab => tab.id === this.activeTab)
          ? 'overview'
          : this.activeTab;
      this.setActiveTab(nextTab);
      return;
    }

    if (wasOnLeads) {
      this.setActiveTab('overview');
      return;
    }

    if (this.tabs.some(tab => tab.id === this.activeTab)) {
      this.setActiveTab(this.activeTab);
      return;
    }

    this.setActiveTab('overview');
  }

  private updateAdvanceContentState(): void {
    const contents = this.campaign?.contents;
    if (!contents) {
      this.hasAdvanceContentFlag = false;
      this.hasLandingPageContentFlag = false;
      return;
    }

    const commonWebsite = findCommonWebsiteContent(contents);
    this.hasLandingPageContentFlag = hasLandingPageApiContent(commonWebsite?.landing_page);
    this.hasAdvanceContentFlag =
      this.hasLandingPageContentFlag ||
      hasSuccessPageApiContent(commonWebsite?.success_page) ||
      hasSmsDeliveryApiContent(findSmsDeliveryContent(contents)) ||
      hasEmailDeliveryApiContent(findDeliveryEmailContent(contents)) ||
      hasMmsDeliveryApiContent(findMmsContent(contents)) ||
      hasRcsDeliveryApiContent(findRcsContent(contents));
  }

  getStatusDisplay(status: string): string {
    return CampaignService.getStatusDisplay(status);
  }

  /**
   * Maps a campaign status string to a CSS class name.
   * @example
   * etStatusClass('active')
   * 'status-active'
   * @param {{string}} {{status}} - The campaign status to map to a CSS class.
   * @returns {{string}} The CSS class name corresponding to the given status, or an empty string if none matches.
   **/
  getStatusClass(status: string): string {
    const statusClassMap: Record<string, string> = {
      active: 'status-active',
      scheduled: 'status-scheduled',
      stopped: 'status-stopped',
      completed: 'status-completed',
      draft: 'status-draft',
      archived: 'status-archived',
      paused: 'status-paused',
    };
    return statusClassMap[status] || '';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
